/**
 * ADR 0046 route-key v1: exactly 22-character unpadded RFC 4648 base64url
 * of the canonical 16 identity bytes. Case-sensitive; not a secret or auth token.
 */

import {
  assertIdentityBytes,
  identityBytesToUlid,
  identityBytesToUuid,
  ulidToIdentityBytes,
  uuidToIdentityBytes,
} from "./identity-bytes";

const BASE64URL =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
const DECODE = new Int16Array(128).fill(-1);
for (let i = 0; i < BASE64URL.length; i++) {
  DECODE[BASE64URL.charCodeAt(i)] = i;
}

export const ROUTE_KEY_V1_LENGTH = 22;

export class RouteKeyV1Error extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RouteKeyV1Error";
  }
}

export function encodeRouteKeyV1(bytes: Uint8Array): string {
  assertIdentityBytes(bytes);
  // 16 bytes → 128 bits → 22 sextets with 2 data bits + 4 zero pad bits in the final character.
  let bitBuffer = 0;
  let bitCount = 0;
  let out = "";
  for (const byte of bytes) {
    bitBuffer = (bitBuffer << 8) | byte;
    bitCount += 8;
    while (bitCount >= 6) {
      bitCount -= 6;
      out += BASE64URL[(bitBuffer >> bitCount) & 0x3f]!;
    }
  }
  if (bitCount > 0) {
    out += BASE64URL[(bitBuffer << (6 - bitCount)) & 0x3f]!;
  }
  return out;
}

export function decodeRouteKeyV1(key: string): Uint8Array {
  if (key.includes("=")) {
    throw new RouteKeyV1Error("Route-key v1 rejects padding");
  }
  if (key.length !== ROUTE_KEY_V1_LENGTH) {
    throw new RouteKeyV1Error(
      `Route-key v1 must be exactly ${ROUTE_KEY_V1_LENGTH} characters; got ${key.length}`,
    );
  }

  let bitBuffer = 0;
  let bitCount = 0;
  const bytes = new Uint8Array(16);
  let byteIndex = 0;

  for (let i = 0; i < key.length; i++) {
    const code = key.charCodeAt(i);
    const value = code < 128 ? DECODE[code]! : -1;
    if (value < 0) {
      throw new RouteKeyV1Error(
        "Route-key v1 rejects characters outside the base64url alphabet",
      );
    }
    bitBuffer = (bitBuffer << 6) | value;
    bitCount += 6;
    if (bitCount >= 8) {
      bitCount -= 8;
      if (byteIndex >= 16) {
        throw new RouteKeyV1Error("Route-key v1 decoded length is not 16 bytes");
      }
      bytes[byteIndex++] = (bitBuffer >> bitCount) & 0xff;
    }
  }

  if (byteIndex !== 16) {
    throw new RouteKeyV1Error("Route-key v1 decoded length is not 16 bytes");
  }

  // 128 bits leave 4 unused bits in the final sextet; they must be zero.
  if (bitCount !== 0) {
    const mask = (1 << bitCount) - 1;
    if ((bitBuffer & mask) !== 0) {
      throw new RouteKeyV1Error(
        "Route-key v1 rejects noncanonical unused tail bits",
      );
    }
  }

  const reencoded = encodeRouteKeyV1(bytes);
  if (reencoded !== key) {
    throw new RouteKeyV1Error(
      "Route-key v1 rejects decode/re-encode mismatch (noncanonical form)",
    );
  }

  return bytes;
}

export function encodeRouteKeyV1FromUuid(uuid: string): string {
  return encodeRouteKeyV1(uuidToIdentityBytes(uuid));
}

export function encodeRouteKeyV1FromUlid(ulid: string): string {
  return encodeRouteKeyV1(ulidToIdentityBytes(ulid));
}

export function routeKeyV1ToUuid(key: string): string {
  return identityBytesToUuid(decodeRouteKeyV1(key));
}

export function routeKeyV1ToUlid(key: string): string {
  return identityBytesToUlid(decodeRouteKeyV1(key));
}
